import {
  InputRequirement,
  QuestionKey,
  RangeInputRequirement,
  SelectRequirement,
  shiftOptions,
  TimeUnit,
  workdayOptions,
} from '@/entities/questionnaire';
import { useUserFeatureToggle, userFeatureToggles } from '@/entities/user';
import { useDictionary } from '@/shared/lib/i18n';
import { Button, Checkbox, DialogV2, Loader, Typography } from '@recruit-robin/robin-theme/web-components';
import { ChevronLeft } from '@recruit-robin/robin-theme/web-icons';
import { useCallback } from 'react';
import { useQuestionnaireForm } from '../../lib/use-questionnaire-form';
import { useReviewQuestionnaireLabels } from '../../lib/use-review-questionnaire-labels';
import { useStyles } from './ReviewQuestionnaireDialog.styles';
import type { ReviewQuestionnaireDialogProps } from './ReviewQuestionnaireDialog.types';
import { ReviewQuestionnaireQuestionWrapper } from './ReviewQuestionnaireQuestionWrapper';

const REVIEW_QUESTION_ORDER: QuestionKey[] = [
  QuestionKey.CERTIFICATES_AND_LICENSES,
  QuestionKey.SALARY,
  QuestionKey.SHIFTS,
  QuestionKey.AVAILABLE_DAYS,
  QuestionKey.WORKING_HOURS_PER_WEEK,
  QuestionKey.LIMITATIONS_AND_PREFERENCES,
  QuestionKey.LANGUAGES,
];

const DontShowQualificationQuestionaryPreview = ({
  dontShowQualificationQuestionaryPreview,
  setDontShowQualificationQuestionaryPreview,
}: {
  dontShowQualificationQuestionaryPreview: boolean;
  setDontShowQualificationQuestionaryPreview: (value: boolean) => void;
}) => {
  const dictionary = useDictionary();

  return (
    <div css={{ display: 'flex' }}>
      <Checkbox
        checked={dontShowQualificationQuestionaryPreview}
        onClick={() => setDontShowQualificationQuestionaryPreview(!dontShowQualificationQuestionaryPreview)}
        label={dictionary.dontShowQualificationQuestionaryPreviewNextTime}
      />
    </div>
  );
};

export function ReviewQuestionnaireDialog({
  isVisible,
  onClose,
  jobId,
  isLoading: externalLoading = false,
  onConfirm,
  onGoBack,
  slot,
}: ReviewQuestionnaireDialogProps) {
  const classes = useStyles({});
  const labels = useReviewQuestionnaireLabels();
  const {
    questions,
    questionnaireState,
    updateQuestionnaireRequirements,
    updateQuestionnaireRequired,
    updateQuestionnaireFieldUnit,
    shouldShowQuestion,
    isLoading: questionnaireLoading,
  } = useQuestionnaireForm({ jobId });

  const {
    checked: dontShowQualificationQuestionaryPreview,
    setChecked: setDontShowQualificationQuestionaryPreview,
    persist: persistQualificationQuestionaryPreviewToggle,
  } = useUserFeatureToggle(userFeatureToggles.dontShowQualificationQuestionaryPreview);

  const handleConfirm = useCallback(() => {
    onConfirm(questionnaireState!);
    persistQualificationQuestionaryPreviewToggle();
  }, [onConfirm, questionnaireState, persistQualificationQuestionaryPreviewToggle]);

  const handleGoBack = useCallback(() => {
    onGoBack();
    persistQualificationQuestionaryPreviewToggle();
  }, [onGoBack, persistQualificationQuestionaryPreviewToggle]);

  const isLoading = questionnaireLoading || !questions;

  if (isVisible && isLoading) {
    return (
      <DialogV2.Root isVisible={true} customCss={classes.dialog}>
        <DialogV2.Backdrop />
        <DialogV2.Content>
          <div css={classes.title}>
            <Typography variant={(v) => v.heading[3]} color={(c) => c.neutral.variant[70]}>
              {labels.loadingTitle}
            </Typography>
          </div>
          <DialogV2.Body>
            <div css={classes.loader}>
              <Loader size={64} />
            </div>
          </DialogV2.Body>
        </DialogV2.Content>
      </DialogV2.Root>
    );
  }

  return (
    <DialogV2.Root isVisible={isVisible} customCss={classes.dialog}>
      <DialogV2.Backdrop />
      <DialogV2.Content customCss={classes.content}>
        <DialogV2.Close onRequestClose={onClose} />
        <DialogV2.Header customCss={classes.header}>
          <Typography variant={(v) => v.heading[3]} color={(c) => c.neutral.variant[80]}>
            {labels.title}
          </Typography>
          <Typography variant={(v) => v.supporting.helper} color={(c) => c.neutral.variant[70]}>
            {labels.subtitle}
          </Typography>
        </DialogV2.Header>

        <DialogV2.Body customCss={classes.body}>
          <div css={classes.questionsList}>
            {REVIEW_QUESTION_ORDER.filter(shouldShowQuestion).map((key) => {
              const question = questions![key];
              return (
                <div key={key} css={classes.questionItem}>
                  <ReviewQuestionnaireQuestionWrapper
                    question={question}
                    getQuestionText={labels.getQuestionText}
                    isRequired={!!question.is_required}
                    onToggleRequired={(checked) => updateQuestionnaireRequired(key, checked)}
                    mustHaveLabel={labels.mustHaveLabel}
                    niceToHaveLabel={labels.niceToHaveLabel}
                    tooltipLabel={labels.tooltipLabel}>
                    {key === QuestionKey.CERTIFICATES_AND_LICENSES && (
                      <InputRequirement
                        value={(question.job_requirements as string[]) ?? []}
                        onChange={(value) => updateQuestionnaireRequirements(key, value)}
                        placeholder={labels.certificationsPlaceholder}
                      />
                    )}
                    {key === QuestionKey.SALARY && (
                      <RangeInputRequirement
                        value={question.job_requirements as { min: number; max: number }}
                        onChange={(value) => updateQuestionnaireRequirements(key, value)}
                        unit={question.unit ?? TimeUnit.MONTH}
                        onUnitChange={(value) => updateQuestionnaireFieldUnit(key, value)}
                        unitOptions={[
                          { label: labels.getTimeUnitFrequency(TimeUnit.HOUR), value: TimeUnit.HOUR },
                          { label: labels.getTimeUnitFrequency(TimeUnit.WEEK), value: TimeUnit.WEEK },
                          { label: labels.getTimeUnitFrequency(TimeUnit.MONTH), value: TimeUnit.MONTH },
                          { label: labels.getTimeUnitFrequency(TimeUnit.YEAR), value: TimeUnit.YEAR },
                        ]}
                      />
                    )}
                    {key === QuestionKey.SHIFTS && (
                      <SelectRequirement
                        value={(question.job_requirements as string[]) ?? []}
                        onChange={(value) => updateQuestionnaireRequirements(key, value)}
                        placeholder={labels.shiftPlaceholder}
                        options={shiftOptions}
                        getLabel={(value) => labels.getShiftLabel(value)}
                      />
                    )}
                    {key === QuestionKey.AVAILABLE_DAYS && (
                      <SelectRequirement
                        value={(question.job_requirements as string[]) ?? []}
                        onChange={(value) => updateQuestionnaireRequirements(key, value)}
                        placeholder={labels.workdaysPlaceholder}
                        options={workdayOptions}
                        getLabel={(value) => labels.getWorkdayLabel(value)}
                      />
                    )}
                    {key === QuestionKey.WORKING_HOURS_PER_WEEK && (
                      <RangeInputRequirement
                        value={question.job_requirements as { min: number; max: number }}
                        onChange={(value) => updateQuestionnaireRequirements(key, value)}
                        unit={TimeUnit.WEEK}
                        isFixedUnit
                      />
                    )}
                    {key === QuestionKey.LIMITATIONS_AND_PREFERENCES && (
                      <InputRequirement
                        value={(question.job_requirements as string[]) ?? []}
                        onChange={(value) => updateQuestionnaireRequirements(key, value)}
                      />
                    )}
                    {key === QuestionKey.LANGUAGES && (
                      <InputRequirement
                        value={(question.job_requirements as string[]) ?? []}
                        onChange={(value) => updateQuestionnaireRequirements(key, value)}
                        placeholder={labels.languagesPlaceholder}
                      />
                    )}
                  </ReviewQuestionnaireQuestionWrapper>
                </div>
              );
            })}
          </div>
          {slot && slot}

          <div css={classes.divider} />

          <DontShowQualificationQuestionaryPreview
            dontShowQualificationQuestionaryPreview={dontShowQualificationQuestionaryPreview}
            setDontShowQualificationQuestionaryPreview={setDontShowQualificationQuestionaryPreview}
          />
          <div css={classes.buttons}>
            <Button
              startIcon={<ChevronLeft size={16} color={(c) => c.neutral.variant[70]} />}
              label={labels.backLabel}
              onClick={handleGoBack}
              style="text"
              type="subtle2"
            />
            <Button
              label={labels.confirmLabel}
              onClick={handleConfirm}
              style="filled"
              type="success"
              disabled={externalLoading}
              startIcon={externalLoading ? <Loader size={16} /> : undefined}
            />
          </div>
        </DialogV2.Body>
      </DialogV2.Content>
    </DialogV2.Root>
  );
}
